import React from 'react';
import { Col, Container, Row } from 'reactstrap';

/**
 * What the site actually does, in the order it happens: ask, record, score.
 *
 * The previous copy sold consensus forecasts from our own premium models. That
 * is not the product — the forecasts come from whichever AI the member already
 * pays for, and the value is in asking consistently and keeping the answer
 * honest afterwards.
 */

const ITEMS = [
    {
        icon: 'ri-file-list-3-line',
        title: 'A consistent way to ask',
        body: `Every prompt is built the same way and states the output it wants — the
               price it found, a range, a confidence, a direction per horizon. Your AI does
               the research: give it one that can search, and it reaches news, filings and
               positioning as of today. Ask the same question twice and the difference is
               the answer, not the question.`,
    },
    {
        icon: 'ri-quill-pen-line',
        title: 'Or bring your own template',
        body: `Prefer your own method? Use it. As long as the answer comes back in the
               same output format — a price range, a confidence, a direction per horizon —
               it is recorded and scored exactly like any other.`,
    },
    {
        icon: 'ri-lock-2-line',
        title: 'A record that cannot drift',
        body: `The window is stamped when the prompt is built, so a forecast cannot be
               backdated into a period that has already played out. There is no editing a
               prediction once it is saved, and none of it can be quietly revised when it
               goes wrong.`,
    },
    {
        icon: 'ri-line-chart-line',
        title: 'See it on the chart',
        body: `Every forecast generates a TradingView indicator — paste it into the Pine
               editor and the window and the range are drawn where the price actually
               went. One button charts all five models at once, each in its own colour,
               which is the quickest way to see whether they disagreed about direction or
               only about how wide to be.`,
    },
    {
        icon: 'ri-robot-line',
        title: 'Any AI, side by side',
        body: `ChatGPT, Claude, Gemini, Grok, DeepSeek, Kimi, Perplexity — paste each one's
               answer against the same prompt and they line up next to each other. Models
               keep getting better; this is how you find out which of yours is.`,
    },
    {
        icon: 'ri-scales-3-line',
        title: 'You settle it, we keep the score',
        body: `When the horizon closes you enter what the asset did — the chart script
               reads the figures back for you — and every model that answered is settled
               at once. From there the arithmetic is ours: did the close land inside the
               stated range, does a 70% confidence hold up 70% of the time, and how far was
               each model's own idea of the price from the truth. Abstentions are recorded
               as abstentions, not counted as misses.`,
    },
    {
        icon: 'ri-flask-line',
        title: 'Four standing experiments',
        body: `Beyond your own questions there are four standard ones the site runs every
               trading day — AI Picked, Event Gaps, Macro Rotation and Extreme Movers. Each
               has a fixed prompt and a fixed way of scoring, so results are comparable
               across models and across months. The prompts are our own research and are
               not published; every run's results are, once it settles.`,
    },
    {
        icon: 'ri-group-line',
        title: 'Your prompts stay yours',
        body: `The prompt behind a forecast belongs to whoever made it. Share a forecast
               and others see its result, its reasoning and how it scored — never the
               prompt. That holds on every plan, and for us too: the standing experiments'
               prompts are not published either, only their version and fingerprint.`,
    },
    {
        icon: 'ri-archive-line',
        title: 'Everything stays searchable',
        body: `Filter your history by symbol, horizon, model or prompt type. The exact
               prompt behind every forecast is kept alongside it, so you can always see
               what the model was actually looking at.`,
    },
];

const Services = () => {
    return (
        <React.Fragment>
            <section className="section bg-light" id="services">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <div className="text-center mb-5">
                                <h1 className="mb-3 ff-secondary fw-semibold lh-base">
                                    Research management for people who use AI
                                </h1>
                                <p className="text-muted">
                                    We do not sell you predictions, and we make no claim about how
                                    accurate your AI is. We give you a standard way to ask, a record
                                    of what was said, and an honest answer about whether it held up.
                                </p>
                            </div>
                        </Col>
                    </Row>

                    <Row className="g-3">
                        {ITEMS.map((item) => (
                            <Col lg={4} md={6} key={item.title}>
                                <div className="d-flex p-3 h-100">
                                    <div className="flex-shrink-0 me-3">
                                        <div className="avatar-sm icon-effect">
                                            <div className="avatar-title bg-transparent text-success rounded-circle">
                                                <i className={`${item.icon} fs-36`}></i>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-grow-1">
                                        <h5 className="fs-18">{item.title}</h5>
                                        <p className="text-muted my-3 ff-secondary">{item.body}</p>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    );
};

export default Services;
